import { Alert, Button, FormControl, InputLabel, Select, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled.tsx";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader.tsx";
import TextFieldExt from "@entrilia/shared/components/inputs/TextFieldExt.tsx";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import { combineValidators, minCharactersValidator, requiredValidator } from "@entrilia/shared/utilities/validators.ts";
import biApi from "../../../../../api/biApi.ts";
import type { ReportGroup, ReportTemplate } from "../../../../../api/types/svTypes.ts";

interface Props {
  organization: string;
  template: ReportTemplate;
  groups: ReportGroup[];
  onChanged: (reportId: string) => void;
  onClose: () => void;
}

export default function DuplicateTemplateDialog({ organization, template, onChanged, onClose, groups }: Props) {
  const [name, setName] = useState(template.name);
  const [groupId, setGroupId] = useState(template.groupId || "");
  const [isFormValid, setIsFormValid] = useState(true);
  const [error, setError] = useState<string>();

  const validateTemplates = useCallback(
    () =>
      biApi.validateTemplateName({
        templateId: undefined,
        name: name,
        organization: organization,
      }),
    [name, organization]
  );

  const duplicateTemplate = useCallback(
    () => biApi.duplicateReportTemplate({ organization, templateId: template.reportId, name, groupId }),
    [template.reportId, name, organization, groupId]
  );

  const { request: duplicateRequest, isLoading: isDuplicating } = useRequest(duplicateTemplate);
  const { request: validateTemplateNameRequest, isLoading: isValidating } = useRequest(validateTemplates);

  const validateTemplateNameAsync = useCallback(async () => {
    const [response, error] = await validateTemplateNameRequest();
    if (error) {
      setError(" An error occurred while duplicating the template.");
      return false;
    }
    if (response && response.nameExists === true) {
      setError("A template with this name already exists");
      return false;
    }
    return true;
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [validateTemplateNameRequest, name]);

  const onSaveClicked = useCallback(async () => {
    const isValid = await validateTemplateNameAsync();
    if (!isValid || !isFormValid) return;

    const [response, error] = await duplicateRequest();
    if (!error) {
      onChanged(response?.reportId || "");
      onClose();
      return;
    }
    setError(" An error occurred while duplicating the template.");
  }, [isFormValid, onChanged, onClose, validateTemplateNameAsync, duplicateRequest]);

  return (
    <PromptDialog open={true} sx={{ p: 0, minHeight: 224 }}>
      <PromptDialogHeader text="Duplicate Template" disabled={isDuplicating} onClose={onClose} />
      {error && (
        <Alert sx={{ mx: 3, mb: 3 }} severity="error">
          {error}
        </Alert>
      )}
      <Stack sx={{ gap: 3, px: 3, pt: 1, pb: 1, flex: 1 }}>
        <TextFieldExt
          name="name"
          textFieldProps={{
            size: "small",
            label: "Name",
            variant: "outlined",
            value: name,
            placeholder: "Set Name...",
            disabled: isDuplicating,
          }}
          doValidate={combineValidators(requiredValidator, minCharactersValidator(2))}
          onValidated={(_, result) => setIsFormValid(result.isValid)}
          onValueChanged={(value) => setName(value)}
        />
        <FormControl fullWidth disabled={isDuplicating}>
          <InputLabel>Group name</InputLabel>
          <Select
            value={groupId}
            label="Group name"
            onChange={(evt) => setGroupId(evt.target.value)}
            disabled={isDuplicating}
          >
            {groups.map((rg) => {
              return (
                <MenuItemStyled key={rg.id} value={rg.id}>
                  <Typography>{rg.caption}</Typography>
                </MenuItemStyled>
              );
            })}
          </Select>
        </FormControl>
      </Stack>

      <DialogButtons onCancel={onClose} cancelDisabled={isDuplicating}>
        <Button
          variant="contained"
          color="primary"
          disabled={isDuplicating || isValidating}
          loading={isDuplicating}
          onClick={onSaveClicked}
        >
          Save
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
}
